import get from 'lodash/get'
import find from 'lodash/find'
import { convertValueToString } from '../utils/typeConverter'
import type { DataStoreMapping } from '../types'
import type { PluginReadyParams } from '@91app/dc-sdk/types'

/**
 * DataStore 訂閱管理服務
 */
export class DataStoreSubscriptionService {
	/**
	 * 用於記錄所有訂閱的監聽器, 以便後續取消訂閱
	 * key: rootPath
	 * value: (value: unknown) => void
	 */
	private unsubscribeMap = new Map<string, (value: unknown) => void>()

	constructor(
		private readonly dataStore: PluginReadyParams['dataStore']
	) {}

	/**
	 * 訂閱所有 DataStore 映射
	 * 因 Design Cloud 機制中，當 rootPath 底下的任一節點有變動時，會直接通知根節點，且會給跟節點整個 rootPath 的值
	 * 所以需要將相同 rootPath 的 mappings 分組成一個 array，並建立一個 DataStore 訂閱監聽器，當 rootPath 底下的任一節點有變動時，會呼叫 callback 函數，並更新 sessionStorage
	 * 此外需要記錄所有訂閱的監聽器，以便後續取消訂閱
	 * 
	 * @param mappings 
	 */
	subscribeDataStore(dataStoreMappings: DataStoreMapping[]): void {
		if (dataStoreMappings.length === 0) return

		const mappingsByRoot = this.groupMappingsByRootPath(dataStoreMappings)

		mappingsByRoot.forEach((groupMappings, rootPath) => {
			const callback = this.createDataStoreListener(groupMappings)
			this.unsubscribeMap.set(rootPath, callback)
			this.dataStore?.subscribe(rootPath, callback)
		})
	}

	/**
	 * 取消所有 DataStore 訂閱
	 */
	unsubscribeAll(): void {
		this.unsubscribeMap.forEach((callback, rootPath) => {
			this.dataStore?.unsubscribe(rootPath, callback)
		})
		this.unsubscribeMap.clear()
	}

	/**
	 * 以 RootPath 為 key 分組 mappings 函數
	 * 
	 * @param mappings 
	 * @returns Map<string, SyncMapping[]>
	 * 
	 * @example
	 * 輸入 mappings = [{ sessionKey: 'userToken', dataStorePath: 'user.token', type: 'string' }, { sessionKey: 'userId', dataStorePath: 'user.id', type: 'number' }]
	 * 輸出 mappingsByRoot = new Map<string, SyncMapping[]>([['user', [{ sessionKey: 'userToken', dataStorePath: 'token', type: 'string' }, { sessionKey: 'userId', dataStorePath: 'id', type: 'number' }]]])
	 */
	private groupMappingsByRootPath(mappings: DataStoreMapping[]) {
		const mappingsByRoot = new Map<string, DataStoreMapping[]>()

		for (const mapping of mappings) {
			if (!mapping.dataStorePath || typeof mapping.dataStorePath !== 'string' || !mapping.sessionKey) {
				continue
			}

			const rootPath = mapping.dataStorePath.split('.')[0]
			if (!rootPath) {
				continue
			}

			const existingMappings = mappingsByRoot.get(rootPath) || []

			if (find(existingMappings, { sessionKey: mapping.sessionKey, dataStorePath: mapping.dataStorePath })) {
				continue
			}

			mappingsByRoot.set(rootPath, [...existingMappings, mapping])
		}
		return mappingsByRoot
	}

	/**
	 * 建立 DataStore 訂閱監聽器 callback 函數
	 * 
	 * @param groupMappings 相同 rootPath 的 mappings
	 * @returns (rootValue: unknown) => void
	 * 
	 */
	private createDataStoreListener(groupMappings: DataStoreMapping[]) {
		return (rootValue: unknown) => {
			for (const mapping of groupMappings) {
				if (!mapping.sessionKey || !mapping.dataStorePath) {
					continue
				}

				try {
					const pathParts = mapping.dataStorePath.split('.')
					const valuePath = pathParts.slice(1).join('.')
					const targetValue = valuePath ? get(rootValue, valuePath) : rootValue
          if (targetValue !== null && targetValue !== undefined) {
            window.sessionStorage.setItem(mapping.sessionKey, convertValueToString(targetValue))
          }
				} catch (error) {
					// Silently handle storage access errors (e.g., storage disabled, quota exceeded)
					// Errors are ignored to prevent breaking other mappings
				}
			}
		}
	}
}
